import {
    combineLatest,
    interval,
    map,
    Subscription,
    Subject,
    debounceTime,
    distinctUntilChanged,
    takeUntil,
    takeWhile,
    filter,
    take,
    timer,
} from 'rxjs';
import {
    NavigationEnd,
    NavigationError,
    NavigationStart,
    RouteConfigLoadEnd,
    RouteConfigLoadStart,
    Router,
} from '@angular/router';
import { trigger, animate, style, transition } from '@angular/animations';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { RightSideBarService } from '@newgenus/angular-shared-components';
import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ParticleService } from './shared/services/particle.service';
import { SecurityService } from './shared/services/security.service';
import { MatSelectionListChange } from '@angular/material/list';
import { ThemeService } from './shared/services/theme.service';
import { BreakpointService } from './shared/services/breakpoint.service';
import { AuthService } from './shared/services/auth.service';
import { LastRouteService } from './shared/services/last-route.service';
import { NavigationHistoryService } from './shared/services/navigation-history.service';
import { StoreUser } from './workspace/user.abstract';
import { displayBasketCount } from './workspace/basket-count';
import { MenuHome } from './shared/utils/constants';
import { SwUpdate } from '@angular/service-worker';
import { MenuConfiguration, MenuItem } from '@newgenus/common';
import { Store } from '@ngrx/store';
import { environment } from '../environments/environment';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { OverlayBackStackService } from '@newgenus/angular-shared-components/services';
import { MobileNavPrefsService } from './shared/services/mobile-nav-prefs.service';
import { MobileMenuSheetComponent, MobileMenuResult } from './components/mobile-menu-sheet/mobile-menu-sheet.component';
import { MobileNavCustomiseSheetComponent } from './components/mobile-menu-sheet/mobile-nav-customise-sheet.component';
import {
    INBASKET_ITEM_ID,
    MOBILE_MENU_ITEM,
    MenuBadgeCounts,
    PENDING_ITEM_ID,
    allDestinations,
    applyBadgeCounts,
    buildBottomBarItems,
    defaultFavouriteIds,
    resolveFavouriteItems,
} from './shared/utils/mobile-nav.util';
import { selectWorkspace } from './redux/workspace/workspace.selectors';
import { NativeShellService } from './shared/services/native-shell.service';
import { PresenceTrackerService } from './shared/services/presence-tracker.service';
import { Capacitor } from '@capacitor/core';
import { shouldReloadForStaleBuild } from './shared/utils/stale-build-reload';

interface AppState {
    message: string;
}

// Test deploy - 2026-03-24
@Component({
    selector: 'newgenus-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [
        // Animate Y axis for the height, and change opacity. From 0 to 100% height.
        trigger('smoothHeight', [
            transition(':leave', [style({ height: '*' }), animate('0.4s ease-out', style({ height: 0 }))]),
            transition(':enter', [style({ height: '0' }), animate('0.4s ease-out', style({ height: '*' }))]),
        ]),
        // Animate opacity with a delay. From 0 to 100% opacity. :leave has no delay, :enter has a 0.4s delay.
        trigger('delayedOpacity', [
            transition(':leave', [style({ opacity: 1 }), animate('0.4s ease-out', style({ opacity: 0 }))]),
            transition(':enter', [style({ opacity: 0 }), animate('0.4s 0.4s ease-out', style({ opacity: 1 }))]),
        ]),
    ],
    standalone: false,
})
export class AppComponent extends StoreUser implements OnDestroy, OnInit {
    // public message$: Observable<string>;

    public settings!: any[];
    public hats!: any[];

    public navigationOptions: MenuItem[] = [];

    /** Permission-filtered menu (sections→items), source for the mobile bar + Menu sheet. */
    private permittedMenu: MenuConfiguration = {};
    /** Flattened destinations of permittedMenu, cached for per-navigation matching. */
    private permittedDestinations: MenuItem[] = [];
    /** The user's pinned favourite ids (synced via MobileNavPrefsService). */
    private favourites: string[] = [];
    /** Live basket tallies for the bar's badges, keyed by MenuItem id. */
    private badgeCounts: MenuBadgeCounts = {};
    /** Computed phone bottom-bar list: [...favourites, Menu]. */
    public mobileBarItems: MenuItem[] = [];
    /** Id of the destination matching the current route (for bar/sheet highlight). */
    public currentDestinationId: string | null = null;
    /** The Menu sentinel id, exposed for the template's selected-state logic. */
    public readonly mobileMenuId = MOBILE_MENU_ITEM.id;

    public subNavSidebarState!: string;
    private msTracker = 0;
    private readonly homeMenu = [MenuHome];
    public selectedOptions: any = this.homeMenu.slice();

    private isFocusMode = false;
    private shouldRefresh = false;
    public hasBackDrop = true;
    public isSmallDisplay = false;
    public loadingRouteConfig = false;
    public showFocusBtn = false;
    public showHeader = true;

    @ViewChild('innerMenuContainer')
    private innerMenuContainer!: MatDrawerContainer;

    @ViewChild('innerMenu', { static: true })
    public innerMenuDrawer!: MatDrawer;

    @ViewChild('drawerSecondary', { static: true })
    public drawerSecondary!: MatDrawer;

    private marginSubscription!: Subscription | null;
    private destroy$ = new Subject<void>();

    constructor(
        private particleService: ParticleService,
        private router: Router,
        private security: SecurityService,
        private themeService: ThemeService,
        protected override store: Store<AppState>,
        public auth: AuthService,
        public sidebarService: RightSideBarService,
        public updates: SwUpdate,
        private lastRouteService: LastRouteService,
        public breakpointService: BreakpointService,
        private bottomSheet: MatBottomSheet,
        private mobileNavPrefs: MobileNavPrefsService,
        // Injected here so it starts tracking navigation from app bootstrap — powers
        // history-aware "Back" buttons (e.g. the email detail view).
        private navigationHistory: NavigationHistoryService,
        // Capacitor native shell — no-ops on web; configures status bar + Android
        // back button and hides the splash once the first navigation settles.
        private nativeShell: NativeShellService,
        private dialog: MatDialog,
        private overlayBack: OverlayBackStackService,
        private zone: NgZone,
        private presenceTracker: PresenceTrackerService
    ) {
        super(store);
        this.initStoreUser(this.destroy$);

        // Online status follows the signed-in session on every page, not just
        // the org dashboard/board that used to connect it.
        this.presenceTracker.start(this.user$);

        // DESIGN.md §Sheets: the system Back closes the top overlay and stays
        // on the page. Dialogs are wired ONCE here rather than per call site —
        // MatDialog is a root singleton, so this catches every dialog in the
        // app AND in the shared lib (the Org Board's edit → Staff → Details
        // chain that answered Back by jumping to the Inbasket). Sheets have no
        // such hook and register at their open sites.
        this.dialog.afterOpened.pipe(takeUntil(this.destroy$)).subscribe((ref) => {
            // A dialog that refuses Escape/backdrop is refusing dismissal —
            // Back must not be a way around it.
            if (!ref.disableClose) this.overlayBack.trackDialog(ref);
        });

        // Keep the phone bottom bar in sync with the user's saved favourites.
        this.mobileNavPrefs.favourites$.pipe(takeUntil(this.destroy$)).subscribe((favourites) => {
            this.favourites = favourites;
            this.recomputeMobileBar();
        });

        // Keep the phone bottom bar's basket badges in sync with the store.
        this.subscribeToBadgeCounts();

        // Expose Router for e2e tests running against the local emulator.
        // Lets Playwright drive in-app navigation without page.goto reloads
        // (which re-race the auth resolver chain). Production builds get no
        // such hook because `useEmulators` is false.
        if (environment.useEmulators) {
            (window as unknown as { _ngRouter?: Router })._ngRouter = this.router;
        }
        // if (environment.emulator_config.useAuthEmulator) {
        //   this.afStorage.storage.useEmulator('localhost', 9199);
        // }

        // Subscribe to the versionUpdates observable to update the application when a new version is available (service worker / PWA).
        this.updates.versionUpdates.subscribe((event) => {
            console.log('SwUpdate.versionUpdates > event:', event);

            if (event.type === 'VERSION_READY') {
                updates.activateUpdate().then(() => {
                    if (window.confirm('An update is available. Please refresh your browser.')) location.reload();
                });
            }
        });
    }

    // ===== Auto-hiding scrollbars (DESIGN.md §Layout RULE 2026-08-10; audit F7) =====
    // Scrollbars show WHILE scrolling and fade at rest, app-wide. One
    // capture-phase listener stamps the active scroller with .ng-is-scrolling;
    // global-styles.scss paints the thin thumb only while stamped (hover also
    // reveals, for desktop mice). Outside the zone — a scroll storm must not
    // run change detection, and the class toggle is direct DOM anyway.
    private scrollFadeTimers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

    private readonly onAnyScroll = (event: Event): void => {
        const el = event.target;
        if (!(el instanceof HTMLElement)) return; // document/root scroll keeps its default bar
        el.classList.add('ng-is-scrolling');
        const prior = this.scrollFadeTimers.get(el);
        if (prior) clearTimeout(prior);
        this.scrollFadeTimers.set(
            el,
            setTimeout(() => {
                el.classList.remove('ng-is-scrolling');
                this.scrollFadeTimers.delete(el);
            }, 800)
        );
    };

    public ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            document.addEventListener('scroll', this.onAnyScroll, { capture: true, passive: true });
        });

        // Capture the previous session's last route SYNCHRONOUSLY now, before
        // startTracking() (fired off user$ below) overwrites localStorage with the
        // boot route. Used only on native to restore the view on cold start.
        const savedRoute = this.nativeShell.isNative ? this.lastRouteService.peekStoredUrl() : null;

        // Native shell (Capacitor): configure status bar + Android back button. All
        // no-ops on web.
        this.nativeShell.init();

        // Keep BOTH native system bars (status + navigation) matched to the in-app
        // theme — light theme → light bars + dark icons, dark theme → dark bars +
        // light icons. Driven off the manual theme toggle so it updates live. (web no-op)
        this.themeService.themeChange$
            .pipe(takeUntil(this.destroy$))
            .subscribe((theme) => this.nativeShell.setSystemBars(String(theme).includes('dark')));

        // Splash reveal + native cold-start route restore. Hold the launch splash
        // until auth state is known. Signed-out → reveal now (login is the
        // destination). Signed-in → once the boot navigation settles on a real screen:
        // on native, if it landed on the default home AND we have a saved route from
        // last session, restore that route first (the WebView always cold-starts at
        // '/', losing the URL the browser would otherwise keep), then reveal — so the
        // splash covers the home→saved transition instead of flashing home. A safety
        // timer guarantees the splash can never stick. (hideSplash no-ops on web.)
        this.auth.afAuth.authState.pipe(take(1), takeUntil(this.destroy$)).subscribe((user) => {
            if (!user) {
                this.revealApp();
                return;
            }
            this.router.events
                .pipe(
                    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                    filter((e) => {
                        const u = e.urlAfterRedirects;
                        return !u.startsWith('/login') && !u.startsWith('/logging-in');
                    }),
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe((e) => {
                    const landed = e.urlAfterRedirects;
                    const canRestore =
                        !!savedRoute &&
                        (!savedRoute.uid || savedRoute.uid === user.uid) &&
                        savedRoute.url !== landed &&
                        (landed === '/app/home' || landed === '/app');
                    if (canRestore) {
                        this.router
                            .navigateByUrl(savedRoute!.url)
                            .catch(() => undefined)
                            .finally(() => this.revealApp());
                    } else {
                        this.revealApp();
                    }
                });
        });
        timer(6000)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.revealApp());

        // This is merely a workaround to adjust the margin of the inner menu container when the sidebar has loaded up.
        this.particleService.sideBarInstantiated$
            .asObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                setTimeout(() => {
                    this.subscribeToUpdateMargin();
                });
            });

        // Subscribe to the user$ observable to update the inner menu container state.
        this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            if (!user) {
                this.innerMenuContainer?.close();
                this.shouldRefresh = true;
            } else if (user !== null && user[0] !== null && this.shouldRefresh) {
                this.innerMenuContainer?.open();
                this.shouldRefresh = false;
            }

            if (user) {
                // Start tracking navigation for last-visited-URL feature
                if (user[0]?.uid) {
                    this.lastRouteService.startTracking(user[0].uid);
                }
                setTimeout(() => this.subscribeToUpdateMargin());
            }
        });

        /**
         * Subscribe to the user$ observable to update the navigation options based on the user's permission and auth state.
         * This is a debounced subscription to prevent the menu from being generated too often.
         */
        // A module-switchboard flip re-runs it too (menus are memoised per switch version).
        combineLatest([this.user$, this.security.moduleVersion$])
            .pipe(
                map(([user]) => user),
                // Coalesce bursts only — menu generation is memoized in SecurityService,
                // and a long debounce here delays the mobile bar/sidebar active highlight.
                debounceTime(100),
                takeUntil(this.destroy$)
            )
            .subscribe(async (user) => {
                if (user && user[0]) {
                    const menu = await this.security.generateSubMenuFromUserAccess();
                    if (menu) {
                        // Set the focus mode to true if the user has access to the focus mode (collapses the sidebar entirely).
                        if (!this.themeService.hasUpdatedFocusBtn) {
                            this.themeService.updateShowFocusBtn(true);
                        }

                        // Set the navigation options to the home menu, top tier menu items, and middle tier menu items.
                        this.navigationOptions = this.homeMenu.concat(
                            menu['HOME'].topTierMenuItems.concat(menu['HOME'].middleTierMenuItems)
                        );

                        // Keep the permission-filtered menu for the mobile bar + Menu sheet.
                        this.permittedMenu = menu;
                        this.recomputeMobileBar();

                        // Update the selection to match the current route (handles restored last-route & direct navigation)
                        setTimeout(() => this.updateMainNavSelection(this.router.url));
                    }
                } else {
                    this.navigationOptions = this.homeMenu;
                    this.permittedMenu = {};
                    this.recomputeMobileBar();
                    this.themeService.updateShowFocusBtn(false);
                    this.themeService.hasUpdatedFocusBtn = false;
                }
            });

        this.themeService.showFocusBtn$
            .asObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe((showFocusBtn) => {
                this.showFocusBtn = showFocusBtn;
            });

        // Subscribe to the localSidebarStatus$ observable to update the sub navigation sidebar state.
        this.themeService.localSidebarStatus$
            .asObservable()
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((sidebarStatus) => {
                // Wrapped in settimeout to help the ngAfterViewChecked lifecycle hook.
                setTimeout(() => {
                    this.subNavSidebarState = sidebarStatus ? 'collapsed' : 'expanded';
                    if (!this.isSmallDisplay) setTimeout(() => this.subscribeToUpdateMargin());
                });
            });

        // Subscribe to the isSmallDisplay$ observable to update the margin of the inner menu container.
        this.themeService.isSmallDisplay$
            .asObservable()
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((isSmallDisplay) => {
                // Wrapped in settimeout to help the ngAfterViewChecked lifecycle hook.
                setTimeout(() => {
                    this.isSmallDisplay = isSmallDisplay;
                    this.subscribeToUpdateMargin();
                });
            });

        this.themeService.isFocusModeEnabled$
            .asObservable()
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((isFocusMode) => {
                this.isFocusMode = isFocusMode;
                if (isFocusMode) {
                    this.innerMenuDrawer.close();
                } else {
                    this.innerMenuDrawer.open();
                }
            });

        // Subscribe to the showHeader$ observable to update the header state.
        this.themeService.showHeader$
            .asObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe((showHeader) => {
                this.showHeader = showHeader;
            });

        // Subscribe to router events to update the main navigation selection.
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.updateMainNavSelection(event.url);
            } else if (event instanceof RouteConfigLoadStart) {
                this.loadingRouteConfig = true;
                // this.flashMessage.show('Loading...')
            } else if (event instanceof RouteConfigLoadEnd) {
                this.loadingRouteConfig = false;
            } else if (event instanceof NavigationError) {
                // A tab older than the last deploy asks for chunks that are gone: reload into the new build.
                try {
                    if (shouldReloadForStaleBuild(event.error, window.sessionStorage, Date.now())) {
                        window.location.assign(event.url);
                    }
                } catch {
                    // sessionStorage itself can throw with site data blocked; stay put rather than risk a loop.
                }
            }
        });

        this.sidebarService.open$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.drawerSecondary.open();
        });

        this.sidebarService.close$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.drawerSecondary.close();
        });
    }

    /**
     * Close the side panel after a drag-down dismiss (OE-162).
     *
     * The drag directive was built for bottom sheets, which are DESTROYED on
     * dismiss — so it leaves its inline transform and `visibility: hidden` on
     * the surface. This panel is a reused drawer: without clearing them it
     * would reopen invisible.
     */
    public closeSidebarPanel(): void {
        this.drawerSecondary.close();
    }

    /**
     * Wipe the drag gesture's inline styles as the panel opens (OE-162).
     *
     * The drag-dismiss directive was written for bottom sheets, which are
     * DESTROYED on dismiss, so it leaves a `translateY(110%)` and
     * `visibility: hidden` behind. This panel is a reused drawer: clearing on
     * the way OUT races the directive's own writes, so it is cleared on the
     * way IN — the moment that actually has to be right.
     */
    public clearSidebarDragStyles(): void {
        const surface = document.querySelector('mat-drawer[position="end"]') as HTMLElement | null;
        if (!surface) return;
        surface.style.transition = '';
        surface.style.transform = '';
        surface.style.visibility = '';
    }

    /**
     * Style both system bars for the current in-app theme, THEN hide the launch
     * splash — so the cold-start changeover (the launch theme defaults to dark icons)
     * happens behind the splash and is never visible. A final pass shortly after
     * guards against the splash-exit resetting the bar appearance. No-ops on web.
     */
    private async revealApp(): Promise<void> {
        const dark = String(this.themeService.themeChange$.value).includes('dark');
        const apply = (): void => void this.nativeShell.setSystemBars(dark);
        // Set the bars WHILE the splash still covers the screen, then lift it — so the
        // changeover happens behind the splash. The splash exit + first window layout
        // can reset the bar appearance to the launch-theme default (dark icons), so
        // re-assert on the window regaining focus AND across a few staggered passes
        // until it settles (this is what was leaving dark icons up for ~3s).
        await this.nativeShell.setSystemBars(dark);
        await this.nativeShell.hideSplash();
        apply();
        window.addEventListener('focus', apply, { once: true });
        [150, 500, 1200].forEach((ms) => setTimeout(apply, ms));
    }

    /**
     * Update the currently selected main navigation item based on the URL.
     *
     * @param {string} newUrl The new URL to check against.
     */
    private updateMainNavSelection(newUrl: string) {
        let moduleFragment = '';

        // If the URL contains '/app/', then the module fragment is the string between the first and second slashes after '/app/'.
        if (newUrl.includes('/app/')) {
            const moduleFragmentStart = newUrl.indexOf('/app/') + 5;
            const moduleFragmentEndIndex = newUrl.indexOf('/', moduleFragmentStart);
            // Setting moduleFragmentEnd to undefined will cause the substring to go to the end of the string, whereas -1 will cause it to invert.
            const moduleFragmentEnd =
                moduleFragmentEndIndex === -1 ? undefined : newUrl.indexOf('/', moduleFragmentStart);
            moduleFragment = newUrl.substring(moduleFragmentStart, moduleFragmentEnd);
        } else {
            // Usually this won't match to anything that's relevant, but it's here just in case.
            moduleFragment = newUrl.substring(
                newUrl.indexOf('/', 1) + 1,
                newUrl.indexOf('/', newUrl.indexOf('/', 1) + 1)
            );
        }

        // Remove the slashes from the module fragment.
        moduleFragment = moduleFragment.replace('/', '').replace('/', '');

        // Update the main navigation menu to show the selected module.
        this.selectedOptions = [this.navigationOptions.find((option: MenuItem) => option.id === moduleFragment)];

        // Resolve the full destination (section + sub-path) for the mobile bar/sheet highlight.
        this.currentDestinationId = this.matchDestinationId(newUrl);
    }

    /**
     * Match a URL to the deepest permitted destination id (section or leaf), for
     * highlighting the active item on the mobile bar / in the Menu sheet.
     */
    private matchDestinationId(url: string): string | null {
        if (!url.includes('/app/')) return null;
        const after = url.substring(url.indexOf('/app/') + 5).split(/[?#]/)[0];
        let best: { id: string; len: number } | null = null;
        for (const d of this.permittedDestinations) {
            const route = d.path ? `${d.section}/${d.path}` : d.section;
            if (after === route || after.startsWith(route + '/')) {
                if (!best || route.length > best.len) best = { id: d.id, len: route.length };
            }
        }
        return best ? best.id : null;
    }

    /** Recompute the phone bottom-bar list from the permitted menu + favourites. */
    private recomputeMobileBar(): void {
        // Cache the flattened destination list — matchDestinationId runs on every
        // NavigationStart and must not rebuild it each time.
        this.permittedDestinations = allDestinations(this.permittedMenu);
        const all = this.permittedDestinations;
        const favIds = this.favourites.length ? this.favourites : defaultFavouriteIds(all);
        const bar = buildBottomBarItems(resolveFavouriteItems(all, favIds));
        this.mobileBarItems = applyBadgeCounts(bar, this.badgeCounts);
    }

    /**
     * Keep the phone bar's basket badges live, straight from the store (OE-97).
     *
     * `workspace/{uid}` carries the authoritative tallies and every basket move
     * corrects them optimistically — the same source the sidebar rows read. The
     * bar takes its own subscription rather than the counts the sidebar writes
     * onto the shared MenuConfigurations objects: those writes are invisible to
     * an OnPush bar, and the sidebar is a drawer on a phone (it need not even be
     * open). Outbasket (basket-3) is not covered here — that tally is derived in
     * the sidebar from a today-only particle filter, not from this doc.
     */
    private subscribeToBadgeCounts(): void {
        this.store
            .select(selectWorkspace)
            .pipe(takeUntil(this.destroy$))
            .subscribe((workspace) => {
                const doc = workspace[0];
                if (!doc) return;

                const inbasket = displayBasketCount(doc.inbasketCount);
                const pending = displayBasketCount(doc.pendingCount);
                if (inbasket === this.badgeCounts[INBASKET_ITEM_ID] && pending === this.badgeCounts[PENDING_ITEM_ID])
                    return;

                this.badgeCounts = { [INBASKET_ITEM_ID]: inbasket, [PENDING_ITEM_ID]: pending };
                this.recomputeMobileBar();
            });
    }

    /** Which bar slot to highlight: the active favourite if pinned, else Menu. */
    public get mobileSelectedId(): string {
        return this.currentDestinationId && this.favourites.includes(this.currentDestinationId)
            ? this.currentDestinationId
            : this.mobileMenuId;
    }

    /** Open the full navigation sheet (sections→items + Customise). */
    private openMobileMenu(): void {
        const ref = this.bottomSheet.open(MobileMenuSheetComponent, {
            data: { menu: this.permittedMenu, currentItemId: this.currentDestinationId },
            panelClass: 'mobile-menu-sheet-panel',
        });
        this.overlayBack.trackSheet(ref);
        ref.afterDismissed().subscribe((result?: MobileMenuResult) => {
            if (!result) return;
            if (result.action === 'navigate') this.navigateToMenuItem(result.item);
            else if (result.action === 'home') this.navigateToMenuItem(MenuHome);
            else if (result.action === 'customise') this.openCustomiseSheet();
        });
    }

    /** Open the favourites picker; persist the chosen ids on save. */
    private openCustomiseSheet(): void {
        // Pre-select the effective bar set: saved favourites, or the seeded defaults
        // if the user hasn't customised yet (keeps the picker consistent with the bar).
        const selectedIds = this.favourites.length
            ? this.favourites
            : defaultFavouriteIds(allDestinations(this.permittedMenu));
        const ref = this.bottomSheet.open(MobileNavCustomiseSheetComponent, {
            data: { menu: this.permittedMenu, selectedIds },
            panelClass: 'mobile-menu-sheet-panel',
        });
        this.overlayBack.trackSheet(ref);
        ref.afterDismissed().subscribe((ids?: string[]) => {
            if (ids) this.mobileNavPrefs.setFavourites(ids);
        });
    }

    /**
     * Subscribe to the interval that updates the margin of the inner menu container.
     *
     * @private
     * @memberof AppComponent
     */
    private subscribeToUpdateMargin(): void {
        if (!this.marginSubscription) {
            this.marginSubscription = interval(15)
                .pipe(takeWhile((i) => i <= 650))
                .subscribe(() => {
                    this.msTracker += 30;
                    this.innerMenuContainer.updateContentMargins();
                    if (this.msTracker >= 650) {
                        this.unsubscribeFromUpdateMargin();
                    }
                });
        } else {
            // Reset tracker, user is obviously unsure if they want the menu open or closed. You won't bug me today, Satan.
            this.msTracker = 0;
        }
    }

    /**
     * Unsubscribe from the interval that updates the margin of the inner menu container.
     *
     * @private
     * @memberof AppComponent
     */
    private unsubscribeFromUpdateMargin(): void {
        if (this.marginSubscription) {
            this.marginSubscription.unsubscribe();
            this.msTracker = 0;
            setTimeout(() => {
                this.marginSubscription = null;
            });
        }
    }

    /**
     * Callback for when the user selects a main navigation item.
     * Navigate to the selected item's path via the router.
     *
     * @param {MatSelectionListChange} event The event emitted by the mat-selection-list.
     * @memberof AppComponent
     */
    public onMainNavSelect(event: MatSelectionListChange): void {
        this.navigateToMenuItem(event.options[0].value);
    }

    /**
     * Callback for the mobile bottom-tab nav (emits a MenuItem directly rather
     * than a MatSelectionListChange). Routes to the same place as the desktop nav.
     *
     * @param {MenuItem} item The tapped navigation item.
     * @memberof AppComponent
     */
    public onMobileNavSelect(item: MenuItem): void {
        if (item.id === this.mobileMenuId) {
            this.openMobileMenu();
            return;
        }
        this.navigateToMenuItem(item);
    }

    private navigateToMenuItem(item: MenuItem): void {
        const path = `app/${item.section}` + (item.path ? `/${item.path}` : '');
        this.router.navigate([path]);
    }

    /**
     * Enable or disable focus mode, which collapses the sidebar entirely.
     *
     * @memberof AppComponent
     */
    public toggleFocusMode(): void {
        this.isFocusMode = !this.isFocusMode;
        this.themeService.manualUpdateOfIsFocusMode(this.isFocusMode);
    }

    public ngOnDestroy(): void {
        document.removeEventListener('scroll', this.onAnyScroll, { capture: true });
        this.scrollFadeTimers.forEach((timer) => clearTimeout(timer));
        this.scrollFadeTimers.clear();
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }
}

// if (typeof Worker !== 'undefined') {
//   // Create a new
//   const worker = new Worker('./workers/hello.worker', { type: 'module' });
//   worker.onmessage = ({ data }) => {
//     console.log(`page got message: ${data}`);
//   };
//   worker.postMessage('hello');
// } else {
//   // Web Workers are not supported in this environment.
//   console.error("Web Workers are not supported in this environment.");
//   // You should add a fallback so that your program still executes correctly.
// }
